import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Users as UsersIcon, Search, Plus, Trash2, UserCheck, UserX } from 'lucide-react'
import { useUsers, useUpdateUser, useDeleteUser, useCreateUser, useRoles } from '@/hooks/data/useUsers'
import { useAlertContext } from '@/providers/AlertProvider'
import StatusBadge from '@/components/platform/StatusBadge'
import UserEditModal from '../../../../components/crud/UserEditModal'
import type { AuthUser, Role } from '@/types/domain'

const PAGE_SIZE = 20

function formatDate(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function AdminUsersPage() {
  const users = useUsers()
  const roles = useRoles()
  const updateUser = useUpdateUser()
  const deleteUser = useDeleteUser()
  const createUser = useCreateUser()
  const { confirm, success, error: showError } = useAlertContext()

  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<Role | ''>('')
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'inactive'>('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [modalUser, setModalUser] = useState<AuthUser | 'new' | null>(null)

  // IdentityService's GET /api/v1/auth/users has no search/status/pagination params (only
  // ?role=) and returns everything — so this filters/paginates the real full list
  // client-side rather than pretending the backend supports server-side paging it doesn't.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (users.data ?? []).filter((u) => {
      if (term && !u.name.toLowerCase().includes(term) && !u.email.toLowerCase().includes(term)) return false
      if (roleFilter && !u.roles.includes(roleFilter)) return false
      if (statusFilter === 'active' && u.active === false) return false
      if (statusFilter === 'inactive' && u.active !== false) return false
      return true
    })
  }, [users.data, search, roleFilter, statusFilter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const resetPaging = () => setPage(1)

  const allOnPageSelected = pageItems.length > 0 && pageItems.every((u) => selected.has(u.id))
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (allOnPageSelected) pageItems.forEach((u) => next.delete(u.id))
      else pageItems.forEach((u) => next.add(u.id))
      return next
    })
  }
  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const bulkSetActive = (active: boolean) => {
    confirm({
      title: active ? 'Activate users' : 'Deactivate users',
      message: `${active ? 'Activate' : 'Deactivate'} ${selected.size} selected user(s)?${active ? '' : ' They will be signed out everywhere immediately.'}`,
      type: 'warning',
      confirmText: active ? 'Activate' : 'Deactivate',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        try {
          await Promise.all(Array.from(selected).map((id) => updateUser.mutateAsync({ id, active })))
          success('Done', `${selected.size} user(s) updated.`)
          setSelected(new Set())
        } catch (err) {
          showError('Failed', (err as Error)?.message || 'Some updates failed.')
        }
      },
    })
  }

  const bulkDelete = () => {
    confirm({
      title: 'Delete users',
      message: `Permanently delete ${selected.size} selected user(s)? This cannot be undone.`,
      type: 'warning',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        try {
          await Promise.all(Array.from(selected).map((id) => deleteUser.mutateAsync(id)))
          success('Deleted', `${selected.size} user(s) deleted.`)
          setSelected(new Set())
        } catch (err) {
          showError('Failed', (err as Error)?.message || 'Some deletions failed.')
        }
      },
    })
  }

  const deleteOne = (u: AuthUser) => {
    confirm({
      title: 'Delete user',
      message: `Permanently delete ${u.name} (${u.email})? This cannot be undone.`,
      type: 'warning',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        try {
          await deleteUser.mutateAsync(u.id)
          success('Deleted', `${u.name} was deleted.`)
        } catch (err) {
          showError('Failed', (err as Error)?.message || 'Failed to delete user.')
        }
      },
    })
  }

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <UsersIcon className="w-6 h-6" /> User Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Admin-only. Roles: {roles.data?.map((r) => r.role).join(', ') || '…'}
          </p>
        </div>
        <button
          onClick={() => setModalUser('new')}
          className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> New user
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              resetPaging()
            }}
            placeholder="Search name or email…"
            className="w-64 rounded-lg border border-gray-300 bg-white py-2 pl-8 pr-3 text-sm dark:border-gray-700 dark:bg-gray-800"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => {
            setRoleFilter(e.target.value as Role | '')
            resetPaging()
          }}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800"
        >
          <option value="">All roles</option>
          {(['admin', 'manager', 'worker', 'customer'] as Role[]).map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as '' | 'active' | 'inactive')
            resetPaging()
          }}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm dark:border-blue-900 dark:bg-blue-900/20">
          <span className="font-medium text-blue-800 dark:text-blue-300">{selected.size} selected</span>
          <button onClick={() => bulkSetActive(true)} className="flex items-center gap-1 text-green-700 hover:underline dark:text-green-400">
            <UserCheck className="h-3.5 w-3.5" /> Activate
          </button>
          <button onClick={() => bulkSetActive(false)} className="flex items-center gap-1 text-amber-700 hover:underline dark:text-amber-400">
            <UserX className="h-3.5 w-3.5" /> Deactivate
          </button>
          <button onClick={bulkDelete} className="flex items-center gap-1 text-red-600 hover:underline">
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </button>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-gray-500 hover:underline">
            Clear
          </button>
        </div>
      )}

      {users.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-10 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
          ))}
        </div>
      ) : users.isError ? (
        <p className="text-sm text-red-600">Failed to load users — requires the admin role.</p>
      ) : filtered.length === 0 ? (
        <p className="p-6 text-sm text-gray-500">No users match these filters.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              <tr>
                <th className="w-10 px-4 py-3">
                  <input type="checkbox" checked={allOnPageSelected} onChange={toggleSelectAll} className="rounded border-gray-300 dark:border-gray-600" />
                </th>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Role(s)</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {pageItems.map((u) => (
                <tr key={u.id} className={selected.has(u.id) ? 'bg-blue-50/50 dark:bg-blue-900/10' : undefined}>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(u.id)}
                      onChange={() => toggleSelect(u.id)}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{u.name}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{u.email}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{u.roles.join(', ')}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={u.active === false ? 'Inactive' : 'Active'} />
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{formatDate(u.createdAt)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={() => setModalUser(u)} className="text-blue-600 hover:underline dark:text-blue-400">
                        Edit
                      </button>
                      <button onClick={() => deleteOne(u)} className="text-red-600 hover:underline">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">
            {filtered.length} user{filtered.length === 1 ? '' : 's'} · page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-gray-200 px-3 py-1.5 disabled:opacity-40 dark:border-gray-700"
            >
              Previous
            </button>
            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-lg border border-gray-200 px-3 py-1.5 disabled:opacity-40 dark:border-gray-700"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <UserEditModal
        isOpen={modalUser !== null}
        onClose={() => setModalUser(null)}
        initial={modalUser && modalUser !== 'new' ? modalUser : undefined}
        onSubmit={async (values) => {
          if (modalUser && modalUser !== 'new') {
            await updateUser.mutateAsync({
              id: modalUser.id,
              name: values.name,
              roles: Array.from(new Set([values.role, ...values.extraRoles.filter((r) => !['admin', 'manager', 'worker', 'customer'].includes(r))])),
              active: values.active,
            })
            success('Saved', `${values.name} was updated.`)
          } else {
            await createUser.mutateAsync({
              email: values.email,
              name: values.name,
              password: values.password,
              role: values.role,
              extraRoles: values.extraRoles,
              active: values.active,
            })
            success('Created', `${values.name} was created.`)
          }
        }}
      />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/users/')({ component: AdminUsersPage })
